import { apiUrl } from "@/utils/api";

/**
 * QBank API Service
 * Handles all Question Bank related API calls
 */

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  "Content-Type": "application/json",
});

/**
 * Fetch QBank statistics for a subscription plan
 * Returns question modes, difficulty levels, sources, and total questions
 * @param {number} subscriptionPlanId
 */
export async function fetchStatistics(subscriptionPlanId) {
  const response = await fetch(
    `${apiUrl}/subscription-plans/${subscriptionPlanId}/question-banks/statistics`,
    {
      method: "GET",
      headers: getAuthHeaders(),
    }
  );
  if (!response.ok) throw new Error("Failed to fetch statistics");
  return response.json();
}

/**
 * Fetch categories/lessons tree for a subscription plan
 * @param {number} subscriptionPlanId
 * @param {object} options - { predefined?: boolean }
 */
export async function fetchCategories(subscriptionPlanId, options = {}) {
  const params = new URLSearchParams();
  if (options.predefined) params.append("predefined", "1");

  const queryString = params.toString();
  const url = `${apiUrl}/subscription-plans/${subscriptionPlanId}/question-banks/categories${
    queryString ? `?${queryString}` : ""
  }`;

  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error("Failed to fetch categories");
  return response.json();
}

/**
 * Validate question count based on selected filters
 * @param {number} subscriptionPlanId
 * @param {object} filters - { lessons, question_modes, difficulty_levels?, sources?, question_types? }
 */
export async function validateCount(subscriptionPlanId, filters) {
  const response = await fetch(
    `${apiUrl}/subscription-plans/${subscriptionPlanId}/question-banks/validate-count`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(filters),
    }
  );
  if (!response.ok) throw new Error("Failed to validate count");
  const json = await response.json();
  // Return the count directly from the response
  // API returns: {"data":{"available_questions_count":134}}
  return (
    json?.data?.available_questions_count ??
    json?.available_questions_count ??
    null
  );
}

/**
 * Create a new custom test/question bank
 * @param {number} subscriptionPlanId
 * @param {object} testData - { lessons, quiz_mode, total_questions, question_modes, difficulty_levels?, sources?, question_types? }
 */
export async function createTest(subscriptionPlanId, testData) {
  const response = await fetch(
    `${apiUrl}/subscription-plans/${subscriptionPlanId}/question-banks`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(testData),
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Failed to create test");
  }
  return response.json();
}

/**
 * Fetch predefined QBank types for filtering
 * @param {number} subscriptionPlanId
 */
export async function fetchPredefinedTypes(subscriptionPlanId) {
  const response = await fetch(
    `${apiUrl}/subscription-plans/${subscriptionPlanId}/predefined-qbanks/types`,
    {
      method: "GET",
      headers: getAuthHeaders(),
    }
  );
  if (!response.ok) throw new Error("Failed to fetch predefined types");
  return response.json();
}

/**
 * Fetch available sources for filtering
 */
export async function fetchSources() {
  const response = await fetch(`${apiUrl}/qbanks/sources`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error("Failed to fetch sources");
  return response.json();
}

/**
 * Fetch predefined QBanks list with optional filters
 * @param {number} subscriptionPlanId
 * @param {object} filters - { predefined_type?, child_category_id?, source_id?, page? }
 */
export async function fetchPredefinedQBanks(subscriptionPlanId, filters = {}) {
  const params = new URLSearchParams();
  if (filters.predefined_type)
    params.append("predefined_type", filters.predefined_type);
  if (filters.child_category_id)
    params.append("child_category_id", filters.child_category_id);
  if (filters.source_id) params.append("source_id", filters.source_id);
  if (filters.page) params.append("page", filters.page);

  const queryString = params.toString();
  const url = `${apiUrl}/subscription-plans/${subscriptionPlanId}/predefined-qbanks${
    queryString ? `?${queryString}` : ""
  }`;

  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error("Failed to fetch predefined QBanks");
  return response.json();
}

/**
 * Clone a predefined QBank to user's account
 * @param {number} subscriptionPlanId
 * @param {number} predefinedQBankId
 */
export async function clonePredefinedQBank(
  subscriptionPlanId,
  predefinedQBankId
) {
  const response = await fetch(
    `${apiUrl}/subscription-plans/${subscriptionPlanId}/predefined-qbanks/${predefinedQBankId}/clone`,
    {
      method: "POST",
      headers: getAuthHeaders(),
    }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Failed to clone QBank");
  }
  return response.json();
}

/**
 * Fetch user's QBanks for a subscription plan
 * @param {number} subscriptionPlanId
 */
export async function fetchUserQBanks(subscriptionPlanId) {
  const response = await fetch(
    `${apiUrl}/subscription-plans/${subscriptionPlanId}/qbanks`,
    {
      method: "GET",
      headers: getAuthHeaders(),
    }
  );
  if (!response.ok) throw new Error("Failed to fetch user QBanks");
  return response.json();
}

/**
 * Replicate/restart a completed test
 * @param {number} qbankId
 */
export async function replicateQBank(qbankId) {
  const response = await fetch(`${apiUrl}/qbanks/${qbankId}/replicate`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error("Failed to replicate QBank");
  return response.json();
}

export default {
  fetchStatistics,
  fetchCategories,
  validateCount,
  createTest,
  fetchPredefinedTypes,
  fetchSources,
  fetchPredefinedQBanks,
  clonePredefinedQBank,
  fetchUserQBanks,
  replicateQBank,
};
